import React, { useEffect, useState } from "react";
import { Save, Truck, MapPin, X, Plus, AlertCircle, Loader2, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

export default function DeliverySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCp, setNewCp] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/delivery-settings");
      setSettings(data);
    } catch (e) {
      toast.error("Impossible de charger la configuration");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/delivery-settings", {
        local_postal_codes: settings.local_postal_codes,
        local_free_delivery_min: settings.local_free_delivery_min,
        national_delivery_fee: settings.national_delivery_fee,
        national_free_delivery_min: settings.national_free_delivery_min,
      });
      setSettings(data);
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const addCp = () => {
    const cp = newCp.trim();
    if (!/^\d{4}$/.test(cp)) {
      toast.error("Code postal invalide (4 chiffres requis)");
      return;
    }
    if ((settings.local_postal_codes || []).includes(cp)) {
      toast.error("Ce code postal est déjà dans la liste");
      return;
    }
    setSettings({
      ...settings,
      local_postal_codes: [...(settings.local_postal_codes || []), cp].sort(),
    });
    setNewCp("");
  };

  const removeCp = (cp) => {
    setSettings({
      ...settings,
      local_postal_codes: (settings.local_postal_codes || []).filter((c) => c !== cp),
    });
  };

  const restoreDefaults = async () => {
    if (!window.confirm("Réinitialiser aux valeurs par défaut (zone Conthey + alentours, seuil 40 CHF) ?")) return;
    setSettings({
      ...settings,
      local_postal_codes: ["1950", "1951", "1955", "1957", "1962", "1963", "1964", "1965", "1967"],
      local_free_delivery_min: 40,
      national_delivery_fee: 10,
      national_free_delivery_min: 70,
    });
    toast.message("Valeurs par défaut chargées. N'oubliez pas de sauvegarder.");
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-24 text-[#575A57]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="delivery-settings-page">
      <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded-md p-4 text-sm text-[#8C6B2F] flex gap-2 items-start">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <strong>Stratégie business :</strong> la livraison locale gratuite encourage les clients de la région à commander en ligne, sans cannibaliser vos distributeurs automatiques. Réglez le seuil minimum pour éviter qu'un client commande une seule barquette à 8 CHF en livraison gratuite.
        </div>
      </div>

      {/* Zone locale */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Zone locale (livraison gratuite possible)</h2>
        </div>
        <p className="text-sm text-[#575A57] mb-4">
          Codes postaux suisses où la livraison est <strong>gratuite à partir du seuil ci-dessous</strong>. Hors de cette zone, c'est le tarif national qui s'applique.
        </p>

        <div className="mb-4">
          <Label className="mb-2 block text-xs uppercase tracking-wider font-semibold text-[#575A57]">
            Codes postaux ({(settings.local_postal_codes || []).length})
          </Label>
          <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-3 bg-[#F9F9F6] border border-[#E5E5E0] rounded" data-testid="cp-chips-container">
            {(settings.local_postal_codes || []).length === 0 ? (
              <span className="text-xs text-[#A3A6A3] italic">Aucun code postal — livraison locale désactivée</span>
            ) : (settings.local_postal_codes || []).map((cp) => (
              <Badge
                key={cp}
                variant="outline"
                className="font-mono bg-white border-[#1F4E3D] text-[#1F4E3D] gap-1 pr-1"
                data-testid={`cp-chip-${cp}`}
              >
                {cp}
                <button
                  type="button"
                  onClick={() => removeCp(cp)}
                  className="hover:bg-[#1F4E3D]/10 rounded p-0.5"
                  title="Supprimer"
                  data-testid={`cp-remove-${cp}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex gap-2 items-end">
          <div className="flex-1 max-w-xs">
            <Label className="mb-1 block text-xs text-[#575A57]">Ajouter un code postal</Label>
            <Input
              value={newCp}
              onChange={(e) => setNewCp(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCp())}
              placeholder="Ex: 1958"
              maxLength={4}
              className="font-mono"
              data-testid="new-cp-input"
            />
          </div>
          <Button
            onClick={addCp}
            variant="outline"
            disabled={newCp.length !== 4}
            data-testid="add-cp-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      {/* Seuils */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Seuils et tarifs</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <Label className="mb-1.5 block text-sm font-medium">
              Seuil livraison <span className="text-[#1F4E3D]">locale</span> gratuite
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.5"
                min="0"
                value={settings.local_free_delivery_min}
                onChange={(e) => setSettings({ ...settings, local_free_delivery_min: parseFloat(e.target.value || 0) })}
                className="font-mono w-32"
                data-testid="local-min-input"
              />
              <span className="text-sm text-[#575A57]">CHF</span>
            </div>
            <p className="text-[11px] text-[#A3A6A3] mt-1">
              En dessous, le tarif standard de livraison s'applique avec un message d'incitation.
            </p>
          </div>
          <div>
            <Label className="mb-1.5 block text-sm font-medium">
              Tarif de livraison (hors zone ou en dessous du seuil)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.5"
                min="0"
                value={settings.national_delivery_fee}
                onChange={(e) => setSettings({ ...settings, national_delivery_fee: parseFloat(e.target.value || 0) })}
                className="font-mono w-32"
                data-testid="national-fee-input"
              />
              <span className="text-sm text-[#575A57]">CHF</span>
            </div>
            <p className="text-[11px] text-[#A3A6A3] mt-1">Coût appliqué pour une livraison La Poste (PostPac Priority).</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block text-sm font-medium">
              Seuil livraison <span className="text-[#5B3A8C]">nationale</span> gratuite (toute la Suisse)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.5"
                min="0"
                value={settings.national_free_delivery_min}
                onChange={(e) => setSettings({ ...settings, national_free_delivery_min: parseFloat(e.target.value || 0) })}
                className="font-mono w-32"
                data-testid="national-min-input"
              />
              <span className="text-sm text-[#575A57]">CHF</span>
            </div>
            <p className="text-[11px] text-[#A3A6A3] mt-1">
              Au-dessus de ce sous-total, la livraison est offerte partout en Suisse même hors zone locale.
            </p>
          </div>
        </div>
      </div>

      {/* Aperçu pédagogique */}
      <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded-lg p-4 text-sm">
        <h3 className="font-heading text-base mb-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-[#1F4E3D]" /> Récap des règles appliquées
        </h3>
        <ul className="space-y-1.5 text-[#3A3A3A] text-[13px]">
          <li>📍 <strong>Zone locale</strong> ({(settings.local_postal_codes || []).length} codes postaux) : <span className="text-[#1F4E3D] font-semibold">gratuit dès {settings.local_free_delivery_min} CHF</span>, sinon {settings.national_delivery_fee} CHF</li>
          <li>🇨🇭 <strong>Reste de la Suisse</strong> : {settings.national_delivery_fee} CHF, gratuit dès {settings.national_free_delivery_min} CHF</li>
          <li>🏪 <strong>Retrait sur place</strong> à Conthey : toujours gratuit</li>
        </ul>
      </div>

      <div className="flex gap-2 justify-between items-center pt-2">
        <Button variant="outline" onClick={restoreDefaults} data-testid="restore-defaults-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Restaurer les valeurs par défaut
        </Button>
        <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
